import { ManagedColumn, ManagedColumnExtra } from 'app/common/table/ManagedTable';
import TimeUtils from 'app/common/util/TimeUtils';
import FlightUtils from 'app/common/util/FlightUtils';
import { FlightFieldType, StandardFlightFieldType } from 'app/model/CORE';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import React, { CSSProperties } from 'react';
import Utils from 'app/common/util/Utils';
import MeasureUtils from 'app/common/util/MeasureUtils';
import { CheckCircleFilled } from '@ant-design/icons';
import AppContext from 'app/common/context/AppContext';
import { ConvectionSource, Sol49FlightDto, Sol49FlightFieldType } from 'app/model/SOL49';
import { DataTable, DataTableProps } from 'app/common/table/DataTable';

import { Flex } from '../common/Flex';
import { FlightMoreMenu } from './FlightMoreMenu';

function toIobtString(obj: Sol49FlightDto) {
    const postFix = obj.proposalContext ? ' P' : obj.proposalInformation ? ' p' : '';
    return TimeUtils.formatDDHHMM(obj.flightId?.flightKeys?.eobt) + postFix;
}

const IOBT_PROPOPSAL_STYLE: CSSProperties = { color: 'red', fontWeight: 'bold' };

function toIobtStyle(value: React.ReactNode, row: Sol49FlightDto): CSSProperties {
    return row.proposalContext || row.proposalInformation ? IOBT_PROPOPSAL_STYLE : undefined;
}

export interface FlightColumnExtra extends ManagedColumnExtra<Sol49FlightDto> {
    neededB2BFields?: Array<FlightFieldType> | (() => Array<FlightFieldType>);
}

/* Flight Columns & Flight Table definitions */
export class FlightColumn extends ManagedColumn<Sol49FlightDto> {
    public constructor(
        header: string,
        width: string,
        getCellValue: (object: Sol49FlightDto) => any,
        extra?: FlightColumnExtra,
    ) {
        super(header, width, getCellValue, extra);
    }

    public static computeB2BFieldList(columns: Array<FlightColumn>): Array<FlightFieldType> {
        const fieldList = new Array<FlightFieldType>();
        columns.forEach(column => {
            const extra = column.extra as FlightColumnExtra;
            if (extra && extra.neededB2BFields) {
                const fields = extra.neededB2BFields instanceof Array ? extra.neededB2BFields : extra.neededB2BFields();
                for (const field of fields) Utils.addIfNeeded(fieldList, field);
            }
        });
        return fieldList;
    }
}

export const FLIGHT_SELECTED_COLUMN = new FlightColumn(
    '',
    '1.5em',
    () => (
        <div className={'check-circle'} style={{ textAlign: 'center' }}>
            <CheckCircleFilled />
        </div>
    ),
    { name: 'selected' },
);

export const FLIGHT_ACTIONS_COLUMN = new FlightColumn(
    '',
    '1em',
    object => (
        <Flex className="more-button" onClick={e => e.stopPropagation()}>
            <FlightMoreMenu flight={object} />
        </Flex>
    ),
    { name: 'actions', className: () => 'flight-actions' },
);

export class StandardFlightColumnV2 {
    public static readonly ADEP = new FlightColumn(
        'ADEP',
        '8ch',
        object => object.flightId.flightKeys && object.flightId.flightKeys.adep,
    );
    public static readonly ADES = new FlightColumn(
        'ADES',
        '8ch',
        object => object.flightId.flightKeys && object.flightId.flightKeys.ades,
    );
    public static readonly ARCID = new FlightColumn(
        'ARCID',
        '8ch',
        object => object.flightId.flightKeys && object.flightId.flightKeys.callsign,
    );
    public static readonly IOBT = new FlightColumn('IOBT', '8ch', toIobtString, {
        neededB2BFields: [StandardFlightFieldType.PROPOSAL_CONTEXT, StandardFlightFieldType.PROPOSAL_INFORMATION],
        cellStyle: toIobtStyle,
    });
    public static readonly xTA = new FlightColumn('E/C/ATA', '6ch', object => object, {
        neededB2BFields: [StandardFlightFieldType.ETA, StandardFlightFieldType.CTA, StandardFlightFieldType.ATA],
        formatter: FlightUtils.getArrivalTimeString,
        comparator: FlightUtils.compareArrivalTime,
    });
    public static readonly DELAY = new FlightColumn(
        'DELAY',
        '2.5em',
        object => object.delayInMs && object.delayInMs !== -1 && (object.delayInMs / TimeUtils.MIN_TO_MS).toString(),
        {
            neededB2BFields: [
                StandardFlightFieldType.DELAY,
                StandardFlightFieldType.SLOT_ISSUED,
                StandardFlightFieldType.CTOT,
            ],
            comparator: Utils.compareStrNumbers,
        },
    );
    public static readonly ATOT_TTOT = new FlightColumn('A/TTOT', '6ch', object => object, {
        neededB2BFields: [
            StandardFlightFieldType.ATOT,
            StandardFlightFieldType.CFMU_FLIGHT_TYPE,
            StandardFlightFieldType.CDM_INFO,
        ],
        formatter: FlightUtils.getFlightAtotTtotString,
        comparator: FlightUtils.compareFlightAtotTtot,
    });
}

export class StandardFlightColumn {
    public static readonly ADEP = new FlightColumn(
        'ADEP',
        '2em',
        object => object.flightId.flightKeys && object.flightId.flightKeys.adep,
    );
    public static readonly ADES = new FlightColumn(
        'ADES',
        '2em',
        object => object.flightId.flightKeys && object.flightId.flightKeys.ades,
    );
    public static readonly AOWIR = new FlightColumn(
        'W',
        '1.5em',
        object => FlightUtils.toAowirShortString(object.reroutable),
        { neededB2BFields: [StandardFlightFieldType.REROUTABLE] },
    );
    public static readonly ARCID = new FlightColumn(
        'ARCID',
        '2.5em',
        object => object.flightId.flightKeys && object.flightId.flightKeys.callsign,
    );
    public static readonly ATA = new FlightColumn('ATA', '2em', object => object.actualTimeOfArrival, {
        neededB2BFields: [StandardFlightFieldType.ATA],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly ATOT = new FlightColumn('ATOT', '5ch', object => object.actualTakeOffTime, {
        neededB2BFields: [StandardFlightFieldType.ATOT],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly ATOT_TTOT = new FlightColumn('A/TTOT', '6ch', object => object, {
        neededB2BFields: [
            StandardFlightFieldType.ATOT,
            StandardFlightFieldType.CFMU_FLIGHT_TYPE,
            StandardFlightFieldType.CDM_INFO,
        ],
        formatter: FlightUtils.getFlightAtotTtotString,
        comparator: FlightUtils.compareFlightAtotTtot,
    });
    public static readonly ATYPE = new FlightColumn('ATY', '3em', object => object.aircraftType, {
        neededB2BFields: [StandardFlightFieldType.AC_TYPE],
    });
    public static readonly AVOIDED_REGULATIONS = new FlightColumn(
        'AVOID REG',
        '5em',
        object => Utils.concat(' ', object.avoidedRegulationIds),
        { neededB2BFields: StandardFlightFieldType.AVOIDED_REGULATIONS },
    );
    public static readonly CDM_EOBT = new FlightColumn('C-EOBT', '3em', object => object.cdmEobt, {
        neededB2BFields: [StandardFlightFieldType.CDM_EOBT],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly CFMU_TYPE = new FlightColumn('T', '1em', object => FlightUtils.getCfmuTypeString(object), {
        neededB2BFields: [StandardFlightFieldType.CFMU_FLIGHT_TYPE, StandardFlightFieldType.ATOT],
    });
    public static readonly CTA = new FlightColumn('CTA', '5ch', object => object.calculatedTimeOfArrival, {
        neededB2BFields: [StandardFlightFieldType.CTA],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly CTOT = new FlightColumn('CTOT', '5ch', object => object.calculatedTakeOffTime, {
        neededB2BFields: [StandardFlightFieldType.CTOT],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly CTOT_LIMIT = new FlightColumn(
        'CL',
        '2em',
        object => FlightUtils.toCtotLimitChar(object.ctotLimitReason),
        { neededB2BFields: [StandardFlightFieldType.CTOT_LIMIT_REASON] },
    );
    public static readonly DELAY = new FlightColumn(
        'DELAY',
        '2em',
        object => object.delayInMs && object.delayInMs !== -1 && (object.delayInMs / TimeUtils.MIN_TO_MS).toString(),
        {
            neededB2BFields: [
                StandardFlightFieldType.DELAY,
                StandardFlightFieldType.SLOT_ISSUED,
                StandardFlightFieldType.CTOT,
            ],
            comparator: Utils.compareStrNumbers,
        },
    );
    public static readonly DELTA_TIME = new FlightColumn(
        'DTime',
        '2.5em',
        object => FlightUtils.getDeltaTimeString(object),
        {
            neededB2BFields: [StandardFlightFieldType.COMPARE_WITH_OTHER_TRAFFIC_TYPE],
            comparator: Utils.compareStrNumbers,
        },
    );
    public static readonly DELTA_LEVEL = new FlightColumn(
        'DLevel',
        '2.5em',
        object => FlightUtils.getDeltaLevelString(object),
        {
            neededB2BFields: [StandardFlightFieldType.COMPARE_WITH_OTHER_TRAFFIC_TYPE],
            comparator: Utils.compareStrNumbers,
        },
    );
    public static readonly DELTA_POSITION = new FlightColumn(
        'DPosn',
        '2.5em',
        object => FlightUtils.getDeltaPosnString(object),
        {
            neededB2BFields: [StandardFlightFieldType.COMPARE_WITH_OTHER_TRAFFIC_TYPE],
            comparator: Utils.compareStrNumbers,
        },
    );
    public static readonly EET = new FlightColumn(
        'EET',
        '2em',
        object =>
            object.estimatedElapsedTimeInMs &&
            Utils.leftPadSpace(object.estimatedElapsedTimeInMs / TimeUtils.MIN_TO_MS, 4),
        { neededB2BFields: [StandardFlightFieldType.EET] },
    );
    public static readonly ENTRY_FL = new FlightColumn(
        'EFL',
        '2em',
        object => FlightLevelUtils.getLevelValue(object.flightLevelAtReferenceLocationEntry),
        { neededB2BFields: [StandardFlightFieldType.FL_AT_REFLOC_ENTRY] },
    );
    public static readonly ENTRY_TIME = new FlightColumn('ENTRY', '2.5em', object => object, {
        neededB2BFields: [StandardFlightFieldType.TIME_AT_REFLOC_ENTRY],
        formatter: (object: Sol49FlightDto) => FlightUtils.getEntryTimeString(object.timeAtReferenceLocationEntry),
        comparator: (f1: Sol49FlightDto, f2: Sol49FlightDto) =>
            TimeUtils.compareWithUndefined(
                f1.timeAtReferenceLocationEntry?.time,
                f2.timeAtReferenceLocationEntry?.time,
            ),
    });
    public static readonly ETA = new FlightColumn('ETA', '2em', object => object.estimatedTimeOfArrival, {
        neededB2BFields: [StandardFlightFieldType.ETA],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly ETOT = new FlightColumn('ETOT', '2.5em', object => object.estimatedTakeOffTime, {
        neededB2BFields: [StandardFlightFieldType.ETOT],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly ETOT_CTOT = new FlightColumn('E/CTOT', '3em', object => object, {
        neededB2BFields: [StandardFlightFieldType.ETOT, StandardFlightFieldType.CTOT],
        formatter: FlightUtils.getEtotCtotString,
        comparator: FlightUtils.compareEtotCtot,
    });
    public static readonly EXCLUDED_REGULATIONS = new FlightColumn(
        'EXC REGS',
        '5em',
        object => Utils.concat(' ', object.excludedRegulationIds),
        { neededB2BFields: [StandardFlightFieldType.EXCLUDED_REGULATIONS] },
    );
    public static readonly EXCLUSION_FROM_REGULATIONS = new FlightColumn(
        'X',
        '1.5em',
        object => FlightUtils.exclusionString(object.exclusionFromRegulations),
        { neededB2BFields: [StandardFlightFieldType.EXCLUSION_FROM_REGULATIONS] },
    );
    public static readonly EXIT_FL = new FlightColumn(
        'XFL',
        '2em',
        object => FlightLevelUtils.getLevelValue(object.flightLevelAtReferenceLocationExit),
        { neededB2BFields: [StandardFlightFieldType.FL_AT_REFLOC_EXIT] },
    );
    public static readonly EXIT_TIME = new FlightColumn('EXIT', '2.5em', object => object, {
        neededB2BFields: [StandardFlightFieldType.TIME_AT_REFLOC_EXIT],
        formatter: (object: Sol49FlightDto) => FlightUtils.getEntryTimeString(object.timeAtReferenceLocationExit),
        comparator: (f1: Sol49FlightDto, f2: Sol49FlightDto) =>
            TimeUtils.compareWithUndefined(f1.timeAtReferenceLocationExit?.time, f2.timeAtReferenceLocationExit?.time),
    });
    public static readonly FUEL_CONSUMPTION = new FlightColumn('FUEL', '2em', object => object.fuelConsumptionInKg, {
        neededB2BFields: [StandardFlightFieldType.FUEL_CONSUMPTION],
    });
    public static readonly ICAO_ROUTE = new FlightColumn('ROUTE', '12.5em', object => object.icaoRoute, {
        neededB2BFields: [StandardFlightFieldType.ICAO_ROUTE],
    });
    public static readonly IOBT = new FlightColumn('IOBT', '2.5em', toIobtString, {
        neededB2BFields: [StandardFlightFieldType.PROPOSAL_CONTEXT, StandardFlightFieldType.PROPOSAL_INFORMATION],
        cellStyle: toIobtStyle,
    });
    public static readonly INTRUDER = new FlightColumn('I', '1.5em', object => FlightUtils.getIntruderString(object), {
        neededB2BFields: [StandardFlightFieldType.COMPARE_WITH_OTHER_TRAFFIC_TYPE],
    });
    public static readonly LAST_VALIDITY = new FlightColumn(
        'LV',
        '2.5em',
        object => FlightUtils.getLastValidityString(object.profileValidity),
        { neededB2BFields: [StandardFlightFieldType.PROFILE_VALIDITY], sortingEnabled: false },
    );
    public static readonly MANUAL_FORCE = new FlightColumn('M', '1.5em', obj => (obj.hasBeenForced ? 'Y' : null), {
        neededB2BFields: [StandardFlightFieldType.HAS_BEEN_FORCED],
    });
    public static readonly MPR = new FlightColumn(
        'REGUL+',
        '4em',
        object => FlightUtils.toMPRWithHasOtherRegulationsString(object),
        { neededB2BFields: [StandardFlightFieldType.MPR, StandardFlightFieldType.HAS_OTHER_REGULATIONS] },
    );
    public static readonly MPR_CAUSE = new FlightColumn(
        'RC',
        '2em',
        object => MeasureUtils.toRegulationCauseShortString(object.mostPenalizingRegulationCause),
        { neededB2BFields: [StandardFlightFieldType.MPR_CAUSE] },
    );
    public static readonly MSG = new FlightColumn('MSG', '2.5em', object => object.lastAtfmMessageType, {
        neededB2BFields: [StandardFlightFieldType.LAST_ATFM_MESSAGE_TYPE],
    });
    public static readonly NUM_OVERLOAD_TVS = new FlightColumn('#OT', '1.5em', object => object.numberOverloadTvs);
    public static readonly OAO = new FlightColumn('OAO', '5em', object => object.operatingAircraftOperator, {
        neededB2BFields: [StandardFlightFieldType.OPERATING_AO],
    });
    public static readonly OTHER_REGS = new FlightColumn(
        'O',
        '1.5em',
        object => (object.hasOtherRegulations ? 'Y' : 'N'),
        { neededB2BFields: [StandardFlightFieldType.HAS_OTHER_REGULATIONS] },
    );
    public static readonly READY = new FlightColumn('S', '1.5em', object => FlightUtils.getReadyShortString(object), {
        neededB2BFields: [StandardFlightFieldType.READY_STATUS],
    });
    public static readonly REG_ID = new FlightColumn('RM', '3em', object => object.filedRegistration, {
        neededB2BFields: [StandardFlightFieldType.FILED_REGISTRATION],
    });
    public static readonly ROUTE_CHARGES = new FlightColumn('RT CHARGES', '3em', object => object.routeChargesInEuros, {
        neededB2BFields: [StandardFlightFieldType.ROUTE_CHARGES],
    });
    public static readonly ROUTE_LEN = new FlightColumn('RL', '2em', object => object.routeLength, {
        neededB2BFields: [StandardFlightFieldType.ROUTE_LENGTH],
    });
    public static readonly RRT_INDICATOR = new FlightColumn(
        'R',
        '1.5em',
        object => FlightUtils.toReroutingIndicatorShortString(object.reroutingIndicator),
        { neededB2BFields: [StandardFlightFieldType.RRT_INDICATOR] },
    );
    public static readonly RFL = new FlightColumn(
        'RFL',
        '2.5em',
        object => FlightLevelUtils.getLevelValue(object.rfl),
        {
            neededB2BFields: [StandardFlightFieldType.RFL],
        },
    );
    public static readonly SIP = new FlightColumn('SIP', '2em', obj => obj.proposalInformation?.proposedCtot, {
        neededB2BFields: [StandardFlightFieldType.PROPOSAL_INFORMATION],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly STATUS = new FlightColumn('STA', '1.5em', obj => FlightUtils.getLateStatusString(obj), {
        neededB2BFields: [StandardFlightFieldType.LATE_FILER, StandardFlightFieldType.LATE_UPDATER],
    });
    public static readonly SUSPEND = new FlightColumn(
        'U',
        '1.5em',
        object => FlightUtils.toSuspensionShortString(object.suspensionStatus),
        { neededB2BFields: [StandardFlightFieldType.SUSPENSION] },
    );
    public static readonly TAXI = new FlightColumn(
        'FTAX',
        '2em',
        object => Math.ceil(object.taxiTimeInMs / TimeUtils.MIN_TO_MS),
        { neededB2BFields: [StandardFlightFieldType.TAXI_TIME] },
    );
    public static readonly TAXI_B = new FlightColumn(
        'TT',
        '2em',
        object => Math.ceil(FlightUtils.getBestTaxiTimeInMs(object) / TimeUtils.MIN_TO_MS),
        {
            neededB2BFields: [
                StandardFlightFieldType.TAXI_TIME,
                StandardFlightFieldType.READY_STATUS,
                StandardFlightFieldType.CURRENT_DEPARTURE_TAXI_TIME_AND_PROCEDURE,
                StandardFlightFieldType.CDM_INFO,
            ],
        },
    );
    public static readonly TAXI_C = new FlightColumn(
        'CTAX',
        '2em',
        object => Math.ceil(object.currentDepartureTaxiTimeAndProcedure?.taxiTimeInMs / TimeUtils.MIN_TO_MS),
        { neededB2BFields: [StandardFlightFieldType.CURRENT_DEPARTURE_TAXI_TIME_AND_PROCEDURE] },
    );
    public static readonly TOBT = new FlightColumn('TOBT', '2.5em', object => object.cdmInfo?.tobt, {
        neededB2BFields: [StandardFlightFieldType.CDM_INFO],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly TSAT = new FlightColumn('TSAT', '2.5em', object => object.cdmInfo?.tsat, {
        neededB2BFields: [StandardFlightFieldType.CDM_INFO],
        formatter: TimeUtils.formatHHMM,
    });
    public static readonly TREND_IN = new FlightColumn(
        'TI',
        '1.5em',
        object => FlightUtils.toTrendIndicator(object.trendAtReferenceLocationEntry),
        { neededB2BFields: [StandardFlightFieldType.TREND_ENTRY] },
    );
    public static readonly TREND_MIDDLE = new FlightColumn(
        'TM',
        '2em',
        object => FlightUtils.toTrendIndicator(object.trendAtReferenceLocationMiddle),
        { neededB2BFields: [StandardFlightFieldType.TREND_MIDDLE] },
    );
    public static readonly TREND_OUT = new FlightColumn(
        'TO',
        '2em',
        object => FlightUtils.toTrendIndicator(object.trendAtReferenceLocationExit),
        { neededB2BFields: [StandardFlightFieldType.TREND_EXIT] },
    );
    public static readonly xTA = new FlightColumn('E/C/ATA', '5ch', object => object, {
        neededB2BFields: [StandardFlightFieldType.ETA, StandardFlightFieldType.CTA, StandardFlightFieldType.ATA],
        formatter: FlightUtils.getArrivalTimeString,
        comparator: FlightUtils.compareArrivalTime,
    });
    public static readonly CONVECTION_IMPACT = new FlightColumn(
        'C.Impact',
        '2.5em',
        object => FlightUtils.getConvectionImpact(object.convectionImpactLevel),
        {
            neededB2BFields: () => {
                let convectionField;
                if (AppContext.getConvectionSource() === ConvectionSource.DWD) {
                    convectionField = Sol49FlightFieldType.DWD_CONVECTION_IMPACT_LEVEL;
                } else if (AppContext.getConvectionSource() === ConvectionSource.METSAFE) {
                    convectionField = Sol49FlightFieldType.METSAFE_CONVECTION_IMPACT_LEVEL;
                } else if (AppContext.getConvectionSource() === ConvectionSource.DWD_ICONV) {
                    convectionField = Sol49FlightFieldType.DWD_ICONV_CONVECTION_IMPACT_LEVEL;
                }
                return [
                    StandardFlightFieldType.FL_AT_REFLOC_ENTRY,
                    StandardFlightFieldType.FL_AT_REFLOC_EXIT,
                    StandardFlightFieldType.TIME_AT_REFLOC_ENTRY,
                    StandardFlightFieldType.TIME_AT_REFLOC_EXIT,
                    convectionField,
                    StandardFlightFieldType.HIGHEST_POINT_PROFILE,
                ];
            },
        },
    );
}

export const FlightTable = (props: DataTableProps<Sol49FlightDto>) => {
    return <DataTable {...props}></DataTable>;
};

export default FlightTable;
